import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, Group, Table, Pagination, Tooltip } from '@mantine/core'
import { AppBadge, AppButton, AppText } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { FilterBar, EmptyState } from '../../app/common/ui'
import type { ModerationLogDto } from '../../app/models/moderation'

function actionColor (action: string): string {
  if (!action) return 'blue'
  const lower = action.toLowerCase()
  if (lower.includes('dismiss')) return 'gray'
  if (lower.includes('warn')) return 'yellow'
  if (lower.includes('remove') || lower.includes('content')) return 'red'
  if (lower.includes('escalate')) return 'violet'
  return 'blue'
}

const TARGET_TYPE_LABEL: Record<number, string> = {
  0: 'Post',
  1: 'Comment',
  2: 'Part',
  3: 'Firearm model',
  4: 'Build'
}

function truncate (s: string, len: number) {
  if (!s) return '—'
  return s.length <= len ? s : s.slice(0, len) + '…'
}

export default observer(function ModerationLogTab () {
  const { moderationStore, userStore } = useStore()
  const { moderationLog, loadingLog, setLogFilter, setLogFilters, exportLog } = moderationStore
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const isAdmin = userStore.user?.isAdmin ?? false

  const handleFilterChange = (key: string, value: unknown) => {
    if (key === 'daterange' && Array.isArray(value)) {
      const [from, to] = value
      setLogFilters({
        dateFrom: from ? (from as Date).toISOString().slice(0, 10) : undefined,
        dateTo: to ? (to as Date).toISOString().slice(0, 10) : undefined
      })
    } else {
      setLogFilter(key, value)
    }
  }

  const filters = [
    { key: 'moderatorUsername', label: 'Moderator', type: 'text' as const },
    {
      key: 'action',
      label: 'Action',
      type: 'select' as const,
      options: [
        { value: '', label: 'All' },
        { value: 'FlagResolved', label: 'Flag Resolved' },
        { value: 'FlagDismissed', label: 'Flag Dismissed' },
        { value: 'FlagEscalated', label: 'Flag Escalated' },
        { value: 'ContentRemoved', label: 'Content Removed' }
      ]
    },
    {
      key: 'targetType',
      label: 'Content Type',
      type: 'select' as const,
      options: [
        { value: '', label: 'All' },
        { value: 'Post', label: 'Post' },
        { value: 'Comment', label: 'Comment' },
        { value: 'Part', label: 'Part' },
        { value: 'FirearmModel', label: 'Firearm model' },
        { value: 'Build', label: 'Build' }
      ]
    },
    { key: 'daterange', label: 'Date range', type: 'daterange' as const }
  ]

  const columns = [
    { key: 'id', label: 'Moderator' },
    { key: 'action', label: 'Action' },
    { key: 'targetType', label: 'Content Type' },
    { key: 'targetTitle', label: 'Content' },
    { key: 'notes', label: 'Notes' },
    { key: 'createdAt', label: 'Date' }
  ]

  const items = moderationLog?.items ?? []
  const totalCount = moderationLog?.totalCount ?? 0
  const pageNumber = moderationLog?.pageNumber ?? 1
  const pageSize = moderationLog?.pageSize ?? 25
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  return (
    <Box>
      <Group position="apart" align="flex-end" mb="md">
        <FilterBar filters={filters} onFilterChange={handleFilterChange} />
        {isAdmin && (
          <AppButton variant="light" size="sm" onClick={() => exportLog()}>
            Export CSV
          </AppButton>
        )}
      </Group>

      {loadingLog && items.length === 0
        ? (
        <Table striped>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key}>
                    <Box component="span" sx={{ display: 'block', height: 20, backgroundColor: '#eee', borderRadius: 4 }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
          )
        : items.length === 0
          ? (
        <EmptyState title="No log entries" message="No moderation log entries match the current filters." />
            )
          : (
        <>
          <Table striped highlightOnHover>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row: ModerationLogDto) => (
                <React.Fragment key={row.id}>
                  <tr
                    key={row.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                  >
                    <td>@{row.moderatorUsername}</td>
                    <td>
                      <AppBadge color={actionColor(row.action)} size="sm">
                        {row.action || '—'}
                      </AppBadge>
                    </td>
                    <td>
                      <AppBadge variant="outline" size="sm">
                        {TARGET_TYPE_LABEL[row.targetType] ?? row.targetType}
                      </AppBadge>
                    </td>
                    <td>{truncate(row.targetTitle, 50)}</td>
                    <td>{truncate(row.notes, 60)}</td>
                    <td>
                      <Tooltip label={new Date(row.createdAt).toLocaleString()} withArrow>
                        <AppText size="sm">{new Date(row.createdAt).toLocaleDateString()}</AppText>
                      </Tooltip>
                    </td>
                  </tr>
                  {expandedId === row.id && (
                    <tr>
                      <td colSpan={columns.length} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: 12 }}>
                        <AppText size="sm" weight={500} mb={4}>
                          Full notes
                        </AppText>
                        <AppText size="sm" sx={{ whiteSpace: 'pre-wrap' }}>
                          {row.notes || '—'}
                        </AppText>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <Pagination
              total={totalPages}
              page={pageNumber}
              onChange={(p) => setLogFilter('pageNumber', p)}
              position="right"
              mt="md"
            />
          )}
        </>
            )}
    </Box>
  )
})
